import { Link } from "react-router-dom";
import {
  UserPlus,
  Database,
  Stethoscope,
  MessageSquareHeart,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Step = {
  icon: React.ComponentType<{ className?: string }>;
  en: string;
  bn: string;
  descEn: string;
  descBn: string;
  timeEn: string;
  timeBn: string;
};

const STEPS: Step[] = [
  {
    icon: UserPlus,
    en: "Sign up in 60 seconds",
    bn: "৬০ সেকেন্ডে সাইন-আপ",
    descEn:
      "Create your chamber with BMDC number, specialty, and clinic address. Demo data is pre-loaded so you can test everything immediately.",
    descBn:
      "BMDC নম্বর, বিশেষত্ব ও ঠিকানা দিয়ে চেম্বার তৈরি করুন। ডেমো ডেটা প্রি-লোডেড — সাথে সাথে পরীক্ষা করুন।",
    timeEn: "~1 min",
    timeBn: "~১ মিনিট",
  },
  {
    icon: Database,
    en: "Import or add your patients",
    bn: "রোগী ইম্পোর্ট করুন",
    descEn:
      "Upload a CSV of existing patients, or add them one-by-one. Our team helps migrate paper registers for free on Pro plans.",
    descBn:
      "CSV আপলোড করুন বা একে একে যোগ করুন। Pro প্ল্যানে কাগজের রেজিস্টার ফ্রি মাইগ্রেশন।",
    timeEn: "Day 1",
    timeBn: "দিন ১",
  },
  {
    icon: Stethoscope,
    en: "Consult with digital Rx & AI",
    bn: "ডিজিটাল Rx ও AI-এর সাথে কনসাল্ট",
    descEn:
      "Write bilingual prescriptions in seconds. The AI scribe drafts SOAP notes; drug-interaction checks run automatically.",
    descBn:
      "সেকেন্ডেই দ্বিভাষিক Rx। AI স্ক্রাইব SOAP নোট ড্রাফট করে; ড্রাগ-ইন্টার‌্যাকশন অটো চেক।",
    timeEn: "Every visit",
    timeBn: "প্রতি ভিজিট",
  },
  {
    icon: MessageSquareHeart,
    en: "Follow up on WhatsApp — automatically",
    bn: "WhatsApp-এ অটো ফলোআপ",
    descEn:
      "Reminders, refill nudges, and care-gap alerts go out on their own. Patients reply — the AI agent books the next visit.",
    descBn:
      "রিমাইন্ডার, রিফিল ও কেয়ার-গ্যাপ অ্যালার্ট নিজেই যায়। রোগী উত্তর দিলে AI এজেন্ট পরের ভিজিট বুক করে।",
    timeEn: "Ongoing",
    timeBn: "চলমান",
  },
];

interface Props {
  className?: string;
  showCta?: boolean;
}

export default function HowItWorks({ className = "", showCta = false }: Props) {
  const { t, lang } = useLang();
  const displayFont =
    lang === "bn"
      ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
      : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  return (
    <section
      aria-label={t("How DoctorsX works", "DoctorsX কীভাবে কাজ করে")}
      className={`max-w-7xl mx-auto px-6 py-16 ${className}`}
    >
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-[#1ba179] bg-[#e6faf3] border border-[#c7f1e2] px-3 py-1.5 rounded-full mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          {t("How it works", "কীভাবে কাজ করে")}
        </div>
        <h2
          className="text-3xl sm:text-4xl font-bold text-[#0c2340] leading-tight"
          style={{ fontFamily: displayFont }}
        >
          {t("From sign-up to sending your first WhatsApp reminder — in one afternoon.", "সাইন-আপ থেকে প্রথম WhatsApp রিমাইন্ডার — এক দুপুরে।")}
        </h2>
        <p className="mt-3 text-[15px] text-[#475569] leading-relaxed">
          {t(
            "Four simple steps. No IT team. No training program. Doctors are seeing patients on DoctorsX the same day.",
            "চারটি সহজ ধাপ। কোনো IT টিম বা প্রশিক্ষণ কর্মসূচি লাগে না। একই দিনেই রোগী দেখা শুরু।",
          )}
        </p>
      </div>

      {/* Steps */}
      <ol className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Connector line (desktop) */}
        <div
          aria-hidden
          className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-[#cbd5e1] to-transparent"
        />
        {STEPS.map((s, i) => (
          <li
            key={s.en}
            className="relative rounded-2xl border border-[#e2e8f0] bg-white p-6 hover:border-[#1ba179] hover:-translate-y-0.5 transition"
          >
            <div className="relative flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1ba179] to-[#0c2340] text-white flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(37,195,154,0.6)]">
                <s.icon className="w-5 h-5" />
              </div>
              <span
                className="text-5xl font-extrabold text-[#e2e8f0] leading-none"
                style={{ fontFamily: displayFont }}
                aria-hidden
              >
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#1ba179]">
              {t(s.timeEn, s.timeBn)}
            </div>
            <h3
              className="mt-1 text-[17px] font-bold text-[#0c2340] leading-snug"
              style={{ fontFamily: displayFont }}
            >
              {t(s.en, s.bn)}
            </h3>
            <p className="mt-2 text-[13px] text-[#64748b] leading-relaxed">
              {t(s.descEn, s.descBn)}
            </p>
          </li>
        ))}
      </ol>

      {showCta && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 bg-[#0c2340] hover:bg-[#0f172a] text-white font-semibold px-6 py-3 rounded-full transition"
          >
            {t("Start free — no card", "ফ্রি শুরু করুন — কার্ড লাগবে না")}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/features"
            className="inline-flex items-center gap-2 border border-[#cbd5e1] hover:border-[#0c2340] text-[#0c2340] font-semibold px-6 py-3 rounded-full transition"
          >
            {t("See all features", "সব ফিচার দেখুন")}
          </Link>
        </div>
      )}
    </section>
  );
}
